import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import type { Link } from '../../Model/links';
import styles from './LinkRow.module.css';

const BLOCK_DURATION = 0.2;

function isRoughlyOnScreen(el: HTMLElement | null): boolean {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
}

export interface LinkRowProps {
    link: Link;
    transitionId: string;
    staggerInMs?: number;
    phase: 'in' | 'out';
    transitionToken: number;
    onHideComplete: (id: string, token: number) => void;
}

const LinkRow: React.FC<LinkRowProps> = ({
    link,
    transitionId,
    staggerInMs = 0,
    phase,
    transitionToken,
    onHideComplete,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const blockerControls = useAnimationControls();
    const [contentOpacity, setContentOpacity] = useState(0);
    const cancelPaintRef = useRef(false);
    const completedHideTokenRef = useRef<number | null>(null);

    const runRevealPaint = useCallback(
        async (shouldWipe: boolean) => {
            cancelPaintRef.current = false;
            await new Promise<void>((resolve) => setTimeout(resolve, staggerInMs));
            if (cancelPaintRef.current) return;

            if (!shouldWipe) {
                blockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
                setContentOpacity(1);
                return;
            }

            await blockerControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (cancelPaintRef.current) return;
            setContentOpacity(1);
            await blockerControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
        },
        [blockerControls, staggerInMs],
    );

    const runHidePaint = useCallback(
        async (shouldWipe: boolean, token: number) => {
            cancelPaintRef.current = false;

            if (!shouldWipe) {
                setContentOpacity(0);
                onHideComplete(transitionId, token);
                return;
            }

            blockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
            await blockerControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (cancelPaintRef.current) return;
            setContentOpacity(0);
            await blockerControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (!cancelPaintRef.current) {
                onHideComplete(transitionId, token);
            }
        },
        [blockerControls, onHideComplete, transitionId],
    );

    useEffect(() => {
        if (phase !== 'in') return;

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(containerRef.current);
        void runRevealPaint(shouldWipe);

        return () => {
            cancelPaintRef.current = true;
            blockerControls.stop();
        };
    }, [blockerControls, phase, runRevealPaint]);

    useEffect(() => {
        if (phase !== 'out') return;

        if (completedHideTokenRef.current === transitionToken) return;
        completedHideTokenRef.current = transitionToken;

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(containerRef.current);
        void runHidePaint(shouldWipe, transitionToken);

        return () => {
            cancelPaintRef.current = true;
            blockerControls.stop();
        };
    }, [blockerControls, phase, runHidePaint, transitionToken]);

    return (
        <div ref={containerRef} className={styles.wrap}>
            <motion.a
                className={styles.panel}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${link.title} (opens in new tab)`}
                initial={{ opacity: 0 }}
                animate={{ opacity: contentOpacity }}
                transition={{ duration: 0 }}
            >
                <img className={styles.icon} src={link.icon} alt="" aria-hidden />
                <span className={styles.title}>{link.title}</span>
                <span className={styles.external} aria-hidden>
                    ↗
                </span>
            </motion.a>
            <motion.div
                className={styles.blocker}
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={blockerControls}
                style={{ transformOrigin: 'left center' }}
            />
        </div>
    );
};

export default LinkRow;
